import { Injectable, inject } from '@angular/core';
import { Observable, from, map } from 'rxjs';
import { PUBLIC_MEDIA_BUCKET, supabase } from '../../core/supabase/supabase.client';
import { CMSSection, Tab } from '../../Pages/custom-home/home/types';
import {
  ImageAssetUploadService,
  type ImageAssetKind,
} from '../utils/image-asset-upload.service';
import { AuthService } from '../auth/auth.service';

interface RawRow {
  id: number;
  [key: string]: any;
}

@Injectable({ providedIn: 'root' })
export class ExperienceService {
  private readonly TABS_TABLE = 'home_tabs';
  private readonly SECTIONS_TABLE = 'home_sections';
  private readonly STORAGE_BUCKET = PUBLIC_MEDIA_BUCKET;

  private readonly imageAssets = inject(ImageAssetUploadService);
  private readonly auth = inject(AuthService);

  /* =========================================================
   * HELPERS
   * ========================================================= */
  private slugify(value?: string) {
    return (value || 'section')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }

  private unwrapConfigData(config: any): any {
    let data = config?.data ?? config ?? {};

    while (
      data &&
      typeof data === 'object' &&
      !Array.isArray(data) &&
      data.data &&
      typeof data.data === 'object' &&
      Object.keys(data).every((key) => key === 'data' || key === 'version')
    ) {
      data = data.data;
    }

    return data || {};
  }

  private mapDbTab(tab: RawRow): Tab {
    const sections = ((tab['sections'] as RawRow[]) || [])
      .sort((a, b) => (a['position'] || 0) - (b['position'] || 0))
      .map((s) => this.mapDbSection(s));

    return {
      id: String(tab.id),
      name: tab['name'] || '',
      slug: tab['slug'] || '',
      icon_url: tab['icon_url'] || tab['meta']?.['icon_url'] || '',
      icon_file_id: tab['icon_file_id'] || tab['meta']?.['icon_file_id'] || null,
      icon_bucket: tab['icon_bucket'] || tab['meta']?.['icon_bucket'] || '',
      icon_path: tab['icon_path'] || tab['meta']?.['icon_path'] || '',
      search_placeholder: tab['search_placeholder'] || '',
      position: tab['position'] || 0,
      is_active: tab['status'] === 'published',
      sections,
      config: tab['meta'] || {},
      theme_name: tab['meta']?.['theme_name'] || tab['theme_name'] || '',
      theme_id: tab['theme_id'] || '',
    };
  }

  private mapDbSection(section: RawRow): CMSSection {
    const rawConfig = section['config'];
    const config = {
      version: rawConfig?.version ?? 1,
      data: this.unwrapConfigData(rawConfig),
    };

    return {
      id: String(section.id),
      tab_id: String(section['home_tab_id']),
      type: section['section_type'],
      name: section['title'],
      position: section['position'] || 0,
      is_active: section['status'] === 'published',
      config,
    };
  }

  private mapTabPayload(tab: Tab, position: number) {
    const isNew = tab.id.startsWith('tab_') || isNaN(Number(tab.id));
    const userId = this.auth.user()?.id;
    const now = new Date().toISOString();

    return {
      ...(isNew ? { created_by: userId, created_at: now } : { id: Number(tab.id) }),
      name: tab.name,
      slug: tab.slug || this.slugify(tab.name),
      search_placeholder: tab.search_placeholder,
      position,
      status: tab.is_active ? 'published' : 'draft',
      updated_by: userId,
      updated_at: now,
      published_at: tab.is_active ? now : null,
      icon_file_id: tab.icon_file_id,
      meta: {
        ...(tab.config || {}),
        theme_name: tab.theme_name,
        // Fallback for URL/Bucket/Path which don't have columns yet
        icon_url: tab.icon_url,
        icon_bucket: tab.icon_bucket,
        icon_path: tab.icon_path,
      },
      theme_id: tab.theme_id,
    };
  }

  private mapSectionPayload(
    section: CMSSection,
    tabDbId: number,
    position: number
  ) {
    const isNew = section.id.startsWith('sec_') || isNaN(Number(section.id));
    const userId = this.auth.user()?.id;
    const now = new Date().toISOString();

    return {
      ...(isNew ? { created_by: userId, created_at: now } : { id: Number(section.id) }),
      home_tab_id: tabDbId,
      title: section.name || section.type,
      subtitle: section.config?.subtitle || null,
      slug: this.slugify(section.name || section.type),
      section_type: section.type,
      layout_type: 'grid',
      position,
      status: section.is_active ? 'published' : 'draft',
      updated_by: userId,
      updated_at: now,
      visibility: 'all',
      config: {
        version: 1,
        data: this.unwrapConfigData(section.config),
      },
    };
  }

  /* =========================================================
   * GET
   * ========================================================= */
  getTabs(): Observable<Tab[]> {
    return from(
      supabase
        .from(this.TABS_TABLE)
        .select(
          `
          *,
          sections:${this.SECTIONS_TABLE}(*)
        `
        )
        .order('position', { ascending: true })
    ).pipe(
      map(({ data, error }) => {
        if (error) throw error;
        return ((data || []) as RawRow[]).map((tab) => this.mapDbTab(tab));
      })
    );
  }

  /* =========================================================
   * SAVE FULL LAYOUT
   * ========================================================= */
  saveLayout(tabs: Tab[]): Observable<{ success: boolean }> {
    return from(this.syncLayout(tabs));
  }

  private async syncLayout(tabs: Tab[]): Promise<{ success: boolean }> {
    const publishTabs = await this.materializePendingUploads(tabs);

    const currentTabIds = new Set<number>();

    for (let i = 0; i < publishTabs.length; i++) {
      const tab = publishTabs[i];

      /* ------------------------------
       * UPSERT TAB
       * ------------------------------ */
      const tabPayload = this.mapTabPayload(tab, i);
      const { data: tabData, error: tabError } = await supabase
        .from(this.TABS_TABLE)
        .upsert(tabPayload)
        .select()
        .single();

      if (tabError) throw tabError;

      const tabDbId = (tabData as RawRow).id;
      currentTabIds.add(tabDbId);

      /* ------------------------------
       * EXISTING SECTIONS
       * ------------------------------ */
      const { data: existingSections, error: sectionFetchError } =
        await supabase
          .from(this.SECTIONS_TABLE)
          .select('id')
          .eq('home_tab_id', tabDbId);

      if (sectionFetchError) throw sectionFetchError;

      const existingIds = new Set<number>(
        (existingSections || []).map((s: any) => s.id)
      );

      const currentIds = new Set<number>(
        tab.sections
          .filter((s) => !s.id.startsWith('sec_') && !isNaN(Number(s.id)))
          .map((s) => Number(s.id))
      );

      /* ------------------------------
       * DELETE REMOVED SECTIONS
       * ------------------------------ */
      const toDelete = [...existingIds].filter((id) => !currentIds.has(id));

      if (toDelete.length) {
        const { error } = await supabase
          .from(this.SECTIONS_TABLE)
          .delete()
          .in('id', toDelete);

        if (error) throw error;
      }

      /* ------------------------------
       * UPSERT SECTIONS
       * ------------------------------ */
      for (let j = 0; j < tab.sections.length; j++) {
        const section = tab.sections[j];
        const payload = this.mapSectionPayload(section, tabDbId, j);

        const { error } = await supabase
          .from(this.SECTIONS_TABLE)
          .upsert(payload);

        if (error) throw error;
      }
    }

    /* ------------------------------
     * DELETE REMOVED TABS
     * ------------------------------ */
    const { data: dbTabs, error: tabFetchError } = await supabase.from(this.TABS_TABLE).select('id');
    if (tabFetchError) throw tabFetchError;

    const dbIds = (dbTabs || []).map((t: any) => t.id);
    const tabsToDelete = dbIds.filter((id: number) => !currentTabIds.has(id));

    if (tabsToDelete.length) {
      await this.deleteTabs(tabsToDelete);
    }

    return { success: true };
  }

  /* =========================================================
   * DELETE
   * ========================================================= */
  deleteTab(tabId: string): Observable<any> {
    return from(this.deleteTabs([Number(tabId)]));
  }

  private async deleteTabs(tabIds: number[]): Promise<{ success: boolean }> {
    // home_sections references home_tabs without ON DELETE CASCADE.
    const { error: sectionError } = await supabase
      .from(this.SECTIONS_TABLE).delete().in('home_tab_id', tabIds);
    if (sectionError) throw sectionError;

    const { error: tabError } = await supabase
      .from(this.TABS_TABLE).delete().in('id', tabIds);
    if (tabError) throw tabError;
    return { success: true };
  }

  deleteSection(sectionId: string): Observable<any> {
    return from(
      supabase.from(this.SECTIONS_TABLE).delete().eq('id', Number(sectionId))
    ).pipe(
      map(({ error }) => {
        if (error) throw error;
        return { success: true };
      })
    );
  }

  /* =========================================================
   * UPLOAD
   * ========================================================= */
  uploadHomeImage(
    file: File,
    entityId: string | number = 'draft'
  ): Observable<{
    file_id: number;
    image_url: string;
    bucket: string;
    path: string;
    variants: Record<string, string>;
    version: string;
  }> {
    return from(this.uploadHomeImageFile(file, entityId));
  }

  private async uploadHomeImageFile(
    file: File,
    entityId: string | number = 'draft',
    kind: ImageAssetKind = 'banner'
  ): Promise<{
    file_id: number;
    image_url: string;
    bucket: string;
    path: string;
    variants: Record<string, string>;
    version: string;
  }> {
    const safeEntityId = String(entityId || 'draft').replace(
      /[^a-zA-Z0-9_-]/g,
      '-'
    );

    const asset = await this.imageAssets.upload({
      file,
      bucket: this.STORAGE_BUCKET,
      basePath: `home/${safeEntityId}`,
      kind,
      entityType: kind === 'home_tab_icon' ? 'home_tab' : 'home_section',
      entityId:
        !isNaN(Number(entityId)) && entityId !== 'draft'
          ? Number(entityId)
          : null,
      purpose: kind === 'home_tab_icon' ? 'icon' : 'cms_image',
    });

    return {
      file_id: asset.fileId,
      image_url: asset.masterUrl,
      bucket: asset.bucket,
      path: asset.masterPath,
      variants: asset.variants,
      version: asset.version,
    };
  }

  /* =========================================================
   * PROCESS PENDING FILES
   * ========================================================= */
  private async materializePendingUploads(tabs: Tab[]): Promise<Tab[]> {
    const uploadNode = async (node: any, entityId: string | number) => {
      if (!node || typeof node !== 'object') return;

      if (node._pending_file instanceof File) {
        const field = node._pending_field || 'image_url';
        const baseField = field.replace('_url', '');

        const kind: ImageAssetKind = /icon/i.test(field)
          ? 'home_tab_icon'
          : /banner|background/i.test(field)
            ? 'banner'
            : 'collection_tile';
        const uploaded = await this.uploadHomeImageFile(
          node._pending_file,
          entityId,
          kind,
        );

        node[field] = uploaded.image_url;
        node[`${baseField}_file_id`] = uploaded.file_id;
        node[`${baseField}_bucket`] = uploaded.bucket;
        node[`${baseField}_path`] = uploaded.path;
        node[`${baseField}_variants`] = uploaded.variants;
        node[`${baseField}_version`] = uploaded.version;
      }

      delete node._pending_file;
      delete node._pending_field;
      delete node._pending_upload_key;

      for (const value of Object.values(node)) {
        if (Array.isArray(value)) {
          for (const item of value) {
            await uploadNode(item, entityId);
          }
        } else if (value && typeof value === 'object') {
          await uploadNode(value, entityId);
        }
      }
    };

    // We do NOT use structuredClone because doing so can sometimes strip
    // the File prototype (or throw DataCloneError on proxies), causing
    // the file uploads to be silently skipped or error out.
    // Mutating the original tabs array is safer and avoids re-uploading
    // files if the user hits "Publish" multiple times in the same session.
    const cloned = tabs;

    for (const tab of cloned) {
      // Process pending tab icon
      if (tab._pending_file instanceof File) {
        const uploaded = await this.uploadHomeImageFile(
          tab._pending_file,
          tab.id,
          'home_tab_icon',
        );
        tab.icon_url = uploaded.image_url;
        tab.icon_file_id = uploaded.file_id;
        tab.icon_bucket = uploaded.bucket;
        tab.icon_path = uploaded.path;
        tab.config = {
          ...(tab.config || {}),
          icon_variants: uploaded.variants,
          icon_version: uploaded.version,
        };
      }
      delete tab._pending_file;

      for (const section of tab.sections || []) {
        await uploadNode(section.config, section.id);
      }
    }

    return cloned;
  }
}
